import {useMemo} from 'react';
import {Controller, UseFormReturn} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import FlatInput from '../../../../../../../components/flat-input/flat-input';
import FlatSelectInput from '../../../../../../../components/flat-select-input/flat-select-input';
import {PhoneNumberInput} from '../../../../../../../components/phone-number-input/phone-number-input';
import {FlatMultiTextSelect} from '../../../../../../../components/flat-multi-text-select/flat-multi-text-select';
import {useFetchAllItems} from '../../../../../../../hooks/fetch-all-items';
import {RolesRepository} from '../../../../../../../repositories/roles';
import {translations} from '../../../../../../../i18n/translations';
import {FULL_NAME_MAX_CHARACTERS} from '../../../../../../../utils/constants';
import {
  CLEAN_STRINGS,
  EMAIL_REGEX,
  QUICK_ACCESS_CODE_REGEX,
} from '../../../../../../../utils/constants/regex';
import {validateMobilePhoneNumber} from '../../../../../../../utils/functions';
import {CollaboratorFormInputs} from './renderer';
import './styles.scss';

interface FormProps {
  entityId: string;
  formInstance: UseFormReturn<CollaboratorFormInputs>;
}

const Form = ({
  entityId,
  formInstance: {
    register,
    getValues,
    setValue,
    watch,
    control,
    trigger,
    formState: {errors},
  },
}: FormProps) => {
  const {t} = useTranslation();

  const {items: roles} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      return RolesRepository.instance.getRoles(entityId, limit, skip);
    },
  });

  const rolesOptions = useMemo(() => {
    if (!roles) {
      return [];
    }

    return roles.map((r) => ({key: r.id!, label: r.name}));
  }, [roles]);

  watch('name');

  const isValidPhoneNumber = () => {
    const [phoneNumber, countryCode] = getValues(['phoneNumber', 'countryCode']);
    const phoneNumberWithCountryCode = `${countryCode.phone}${phoneNumber?.trim()}`;
    const phoneNumberValidation = validateMobilePhoneNumber(phoneNumberWithCountryCode);

    if (!phoneNumberValidation.isMobilePhoneNumber || !phoneNumberValidation.isValidPhoneNumber) {
      return false;
    }

    return true;
  };

  return (
    <form id="add-collaborator-form">
      <FlatInput
        label={t(
          translations.professional_caregivers.root.modals.add_staff_flow.form.inputs.name.label
        )}
        placeholder={t(
          translations.professional_caregivers.root.modals.add_staff_flow.form.inputs.name
            .placeholder
        )}
        {...register('name', {
          required: {
            value: true,
            message: t(
              translations.professional_caregivers.root.modals.add_staff_flow.form.inputs.name
                .required_message
            ),
          },
          pattern: {
            value: CLEAN_STRINGS,
            message: t(
              translations.professional_caregivers.root.modals.add_staff_flow.form.inputs.name
                .pattern_message
            ),
          },
          maxLength: {
            value: FULL_NAME_MAX_CHARACTERS,
            message: t(
              translations.professional_caregivers.root.modals.add_staff_flow.form.inputs.name
                .max_message,
              {
                characters: FULL_NAME_MAX_CHARACTERS,
              }
            ),
          },
        })}
        errors={errors.name?.message}
        counter={{
          current: getValues('name')?.length ?? 0,
          max: FULL_NAME_MAX_CHARACTERS,
        }}
        required
      />

      <Controller
        name="roles"
        control={control}
        render={({field: {onChange, value}, fieldState: {error}}) => (
          <FlatMultiTextSelect
            label={t(
              translations.professional_caregivers.root.modals.add_staff_flow.form.inputs.roles
                .label
            )}
            placeholder={t(
              translations.professional_caregivers.root.modals.add_staff_flow.form.inputs.roles
                .placeholder
            )}
            options={rolesOptions ?? []}
            {...{onChange, value}}
            errors={error?.message}
          />
        )}
      />

      <Controller
        name="invitationType"
        control={control}
        rules={{
          required: {
            value: true,
            message: t(
              translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
                .invitation_type.required_message
            ),
          },
        }}
        render={({field: {onChange, value}, fieldState: {error}}) => (
          <FlatSelectInput
            label={t(
              translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
                .invitation_type.label
            )}
            placeholder={t(
              translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
                .invitation_type.placeholder
            )}
            // displayEmpty
            options={[
              {
                value: 'email',
                label: t(
                  translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
                    .invitation_type.via_email
                ),
              },
              {
                value: 'phoneNumber',
                label: t(
                  translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
                    .invitation_type.via_sms
                ),
              },
              {
                value: 'quickAccessCode',
                label: t(
                  translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
                    .invitation_type.via_quickAccessCode
                ),
              },
            ]}
            {...{onChange, value}}
            errors={error?.message}
            required
          />
        )}
      />

      {watch('invitationType') === 'email' && (
        <FlatInput
          label={t(
            translations.professional_caregivers.root.modals.add_staff_flow.form.inputs.email.label
          )}
          placeholder={t(
            translations.professional_caregivers.root.modals.add_staff_flow.form.inputs.email
              .placeholder
          )}
          {...register('email', {
            required: {
              value: true,
              message: t(
                translations.professional_caregivers.root.modals.add_staff_flow.form.inputs.email
                  .required_message
              ),
            },
            pattern: {
              value: EMAIL_REGEX,
              message: t(
                translations.professional_caregivers.root.modals.add_staff_flow.form.inputs.email
                  .pattern_message
              ),
            },
          })}
          errors={errors.email?.message}
          required
        />
      )}

      {watch('invitationType') === 'phoneNumber' && (
        <Controller
          name="phoneNumber"
          control={control}
          rules={{
            required: {
              value: true,
              message: t(
                translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
                  .phone_number.required_message
              ),
            },
            validate: {
              isValidPhoneNumber: () =>
                isValidPhoneNumber() ||
                t(
                  translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
                    .phone_number.is_valid_message
                ),
            },
          }}
          render={({field: {value, onBlur}, fieldState: {error}}) => (
            <PhoneNumberInput
              label={t(
                translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
                  .phone_number.label
              )}
              placeholder={t(
                translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
                  .phone_number.placeholder
              )}
              countryCode={getValues('countryCode')}
              phoneNumber={value}
              handleChangeCountryCode={(value) => {
                setValue('countryCode', value);
                trigger('phoneNumber');
              }}
              handleChangePhoneNumber={(value) =>
                setValue('phoneNumber', value, {shouldValidate: true})
              }
              handleBlur={onBlur}
              error={error?.message}
              required
            />
          )}
        />
      )}

      {watch('invitationType') === 'quickAccessCode' && (
        <FlatInput
          type="password"
          autoComplete="one-time-code"
          label={t(
            translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
              .quick_access_code.label
          )}
          placeholder={t(
            translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
              .quick_access_code.placeholder
          )}
          {...register('quickAccessCode', {
            required: {
              value: true,
              message: t(
                translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
                  .quick_access_code.required_message
              ),
            },
            pattern: {
              value: QUICK_ACCESS_CODE_REGEX,
              message: t(
                translations.professional_caregivers.root.modals.add_staff_flow.form.inputs
                  .quick_access_code.is_valid_message
              ),
            },
          })}
          errors={errors.quickAccessCode?.message}
          required
        />
      )}
    </form>
  );
};

export default Form;
